/**
 * INT-1128: control-plane surface for reopening decided financial-aid applications.
 *
 * An administrator finds the decided application, selects it, states why it is
 * being reopened, and confirms. The backend enforces the admin/owner-only gate,
 * the mandatory reason, and the audit trail — this surface only presents them.
 */
import { useMemo, useState } from 'react';
import { RotateCcw, Search } from 'lucide-react';

import { AppDialogFrame } from '../../components/overlays/AppDialogFrame';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';
import { Card, SectionHeader } from '../school-settings-v2/components/ui';
import { useListFinancialAidApplicationsForReviewQuery } from '../../state/domainRouterApi';
import { useReopenFinancialAidApplication } from '../../financial-aid/hooks/useFinancialAidApi';
import { asArray, asRecord } from '../school-financial-aid-v2-1/utils/domainData';
import { toFinancialAidStaffError } from '../school-financial-aid-v2-1/utils/errorPresentation';

/**
 * Statuses the backend permits reopening. Mirrors `REOPENABLE_STATUSES` in
 * `@triedge/financial-aid-store`; the backend re-checks, this only filters the list.
 */
const REOPENABLE_STATUSES = [
  'approved',
  'denied',
  'award_accepted',
  'award_declined',
] as const;

type ReopenableStatus = (typeof REOPENABLE_STATUSES)[number];

/** Mirrors `REOPEN_REASON_MIN_LENGTH` in the store. */
const REASON_MIN_LENGTH = 10;

const STATUS_LABELS: Record<ReopenableStatus, string> = {
  approved: 'Approved',
  denied: 'Denied',
  award_accepted: 'Award accepted',
  award_declined: 'Award declined',
};

type DecidedApplication = {
  id: string;
  code: string;
  familyName: string;
  academicYear: string;
  status: ReopenableStatus;
};

const readString = (value: unknown): string => String(value ?? '').trim();

const isReopenableStatus = (value: string): value is ReopenableStatus =>
  (REOPENABLE_STATUSES as readonly string[]).includes(value);

const projectDecidedApplication = (item: unknown): DecidedApplication | null => {
  const record = asRecord(item);
  const id = readString(record.id) || readString(record.applicationId);
  const status = readString(record.status).toLowerCase();
  if (!id || !isReopenableStatus(status)) return null;

  const family = asRecord(record.family);
  const applicant = asRecord(record.applicant);
  const familyLink = asRecord(record.familyLink);

  return {
    id,
    code: readString(record.code) || id,
    familyName:
      readString(record.familyName)
      || readString(family.familyName)
      || readString(applicant.familyName)
      || readString(familyLink.familyName)
      || 'Unnamed family',
    academicYear: readString(record.academicYear),
    status,
  };
};

export const FinancialAidReopenPage = () => {
  const reviewQueue = useListFinancialAidApplicationsForReviewQuery({ limit: 500 });
  const reopenMutation = useReopenFinancialAidApplication();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DecidedApplication | null>(null);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const decidedApplications = useMemo<DecidedApplication[]>(
    () =>
      asArray<unknown>(asRecord(reviewQueue.data).items)
        .map(projectDecidedApplication)
        .filter((entry): entry is DecidedApplication => entry !== null),
    [reviewQueue.data],
  );

  const visibleApplications = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return decidedApplications;
    return decidedApplications.filter((application) =>
      [application.familyName, application.code, application.academicYear]
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [decidedApplications, search]);

  const queueError = reviewQueue.error
    ? toFinancialAidStaffError(reviewQueue.error, 'Unable to load decided applications right now.')
    : null;
  const reopenError = reopenMutation.error
    ? toFinancialAidStaffError(reopenMutation.error, 'Unable to reopen this application right now.')
    : null;

  const trimmedReason = reason.trim();
  const canConfirm =
    Boolean(selected) && trimmedReason.length >= REASON_MIN_LENGTH && !reopenMutation.isPending;

  const closeDialog = () => {
    setSelected(null);
    setReason('');
    reopenMutation.reset();
  };

  const handleConfirm = async () => {
    if (!selected || !canConfirm) return;
    try {
      await reopenMutation.mutateAsync({
        applicationId: selected.id,
        reason: trimmedReason,
      });
      setFeedback(
        `${selected.familyName} (${selected.code}) was reopened for review. The prior decision is preserved in its history.`,
      );
      closeDialog();
    } catch {
      // The error surfaces through `reopenMutation.error` inside the dialog.
    }
  };

  return (
    <div className="space-y-5" data-testid="super-administrator-control-plane-financial-aid-reopen">
      <Card>
        <SectionHeader
          title="Reopen Financial Aid Application"
          description="Return a decided application to review. The prior decision is preserved as history, and the reason you give is recorded in the audit trail."
        />

        {feedback ? (
          <p
            className="mb-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm font-semibold text-irongrey"
            role="status"
            data-testid="financial-aid-reopen-feedback"
          >
            {feedback}
          </p>
        ) : null}

        {queueError ? (
          <div data-testid="financial-aid-reopen-queue-error">
            <SupportErrorMessage technicalDetails={queueError.technicalDetails}>
              {queueError.message}
            </SupportErrorMessage>
          </div>
        ) : null}

        <label className="relative block max-w-md">
          <span className="sr-only">Search decided applications</span>
          <Search
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by family, code or academic year"
            className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
            data-testid="financial-aid-reopen-search"
          />
        </label>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs font-semibold uppercase tracking-[0.12em] text-text-tertiary">
                <th scope="col" className="px-3 py-2">Family</th>
                <th scope="col" className="px-3 py-2">Code</th>
                <th scope="col" className="px-3 py-2">Year</th>
                <th scope="col" className="px-3 py-2">Decision</th>
                <th scope="col" className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="financial-aid-reopen-list">
              {reviewQueue.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-text-secondary">
                    Loading decided applications…
                  </td>
                </tr>
              ) : visibleApplications.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-text-secondary"
                    data-testid="financial-aid-reopen-empty"
                  >
                    {decidedApplications.length === 0
                      ? 'No decided applications to reopen.'
                      : 'No decided applications match this search.'}
                  </td>
                </tr>
              ) : (
                visibleApplications.map((application) => (
                  <tr
                    key={application.id}
                    className="border-b border-border/60 last:border-b-0"
                    data-testid={`financial-aid-reopen-row-${application.id}`}
                  >
                    <td className="px-3 py-3 font-semibold text-irongrey">{application.familyName}</td>
                    <td className="px-3 py-3 font-mono text-xs text-text-secondary">{application.code}</td>
                    <td className="px-3 py-3 text-text-secondary">{application.academicYear || '—'}</td>
                    <td className="px-3 py-3 text-text-secondary">{STATUS_LABELS[application.status]}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setFeedback(null);
                          setReason('');
                          reopenMutation.reset();
                          setSelected(application);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-white px-3 py-1.5 text-xs font-bold text-primary transition-all hover:border-primary/40 hover:bg-primary/5"
                        data-testid={`financial-aid-reopen-action-${application.id}`}
                      >
                        <RotateCcw size={14} aria-hidden="true" />
                        Reopen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AppDialogFrame
        open={Boolean(selected)}
        onClose={closeDialog}
        title="Reopen this application?"
        description={
          selected
            ? `${selected.familyName} (${selected.code}) is currently ${STATUS_LABELS[selected.status].toLowerCase()}. Reopening returns it to review. The prior decision and any award already communicated are preserved as history.`
            : ''
        }
        testId="financial-aid-reopen-confirm-modal"
        maxWidthClassName="max-w-xl"
        footer={
          <>
            <button
              type="button"
              onClick={closeDialog}
              disabled={reopenMutation.isPending}
              className="px-4 py-2 text-sm font-bold text-irongrey/60 transition-colors hover:text-irongrey disabled:opacity-50"
              data-testid="financial-aid-reopen-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={!canConfirm}
              className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="financial-aid-reopen-confirm"
            >
              <RotateCcw size={16} aria-hidden="true" />
              {reopenMutation.isPending ? 'Reopening…' : 'Reopen for review'}
            </button>
          </>
        }
      >
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
            Reason for reopening
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] text-primary">
              Required
            </span>
          </span>
          <textarea
            required
            aria-required="true"
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this decision being revisited? This is recorded permanently in the application's history."
            className="mt-2 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold leading-6 text-irongrey outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10"
            data-testid="financial-aid-reopen-reason"
          />
          <span className="mt-1 block text-xs text-text-tertiary" data-testid="financial-aid-reopen-reason-hint">
            At least {REASON_MIN_LENGTH} characters.
          </span>
        </label>

        {reopenError ? (
          <div data-testid="financial-aid-reopen-error">
            <SupportErrorMessage technicalDetails={reopenError.technicalDetails}>
              {reopenError.message}
            </SupportErrorMessage>
          </div>
        ) : null}
      </AppDialogFrame>
    </div>
  );
};
